// CreateDishScreen.js
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import mystyles from '../../assets/styles/mystyles';
import {SafeAreaView} from 'react-native-safe-area-context';

const ENDPOINT_URL = 'https://api.eabu.net';


function CreateDishScreen({ navigation }) {
  // Prefill with sensible defaults matching the example payload
  const [zipcode, setZipcode] = useState('');
  const [service, setService] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [status, setStatus] = useState('');

  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isZipValid = useMemo(() => /^\d{5}$/.test(zipcode), [zipcode]);
  const isStatusValid = useMemo(
    () => ['Open', 'Closed'].includes(status.trim()),
    [status]
  );

  const validate = () => {
    if (!isZipValid) {
      setMessage('Please enter a valid 5-digit ZIP code.');
      return false;
    }
    if (!service.trim()) {
      setMessage('Please enter a service.');
      return false;
    }
    if (!description.trim()) {
      setMessage('Please enter a description.');
      return false;
    }
    if (!address.trim()) {
      setMessage('Please enter an address.');
      return false;
    }
    if (!isStatusValid) {
      setMessage('Status must be either "Open" or "Closed".');
      return false;
    }
    setMessage('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      const url = `${ENDPOINT_URL}/dish/create`;

      const payload = {
        zipcode: zipcode.trim(),
        service: service.trim(),
        description: description.trim(),
        address: address.trim(),
        status: status.trim(),
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add Authorization or other headers if required:
          // Authorization: 'Bearer <TOKEN>',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let details = '';
        try {
          const err = await response.json();
          details = err?.message || JSON.stringify(err);
        } catch {
          // ignore JSON parse error
        }
        throw new Error(`HTTP ${response.status}${details ? `: ${details}` : ''}`);
      }

      Alert.alert('Success', 'Dish saved successfully.');
      // Optionally reset or navigate
      // setZipcode('');
      // setService('');
      // setDescription('');
      // setAddress('');
      // setStatus('Open');
      if (navigation?.goBack) navigation.goBack();
    } catch (e) {
      setMessage(e?.message || 'Failed to save dish. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    isZipValid &&
    !!service.trim() &&
    !!description.trim() &&
    !!address.trim() &&
    isStatusValid &&
    !submitting;

  return (
    <SafeAreaView style={mystyles.safe}>
      <KeyboardAvoidingView
        style={mystyles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          contentContainerStyle={mystyles.scroll}
          keyboardShouldPersistTaps="handled"
        >
          <View style={mystyles.card}>
            <Text style={mystyles.title}>Create Dish</Text>

            <TextInput
              style={mystyles.input}
              placeholder="ZIP Code (e.g. 19060)"
              value={zipcode}
              onChangeText={(text) => setZipcode(text.replace(/[^0-9]/g, '').slice(0, 5))}
              keyboardType="number-pad"
              maxLength={5}
              placeholderTextColor="#8A8A8E"
              returnKeyType="next"
            />

            <TextInput
              style={mystyles.input}
              placeholder="Service (e.g. All Food)"
              value={service}
              onChangeText={setService}
              autoCapitalize="words"
              autoCorrect
              placeholderTextColor="#8A8A8E"
              returnKeyType="next"
            />

            <TextInput
              style={[mystyles.input, { height: 120, textAlignVertical: 'top' }]}
              placeholder="Description (e.g. Cheese Rovioli)"
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={6}
              autoCapitalize="sentences"
              autoCorrect
              placeholderTextColor="#8A8A8E"
            />


            <TextInput
              style={mystyles.input}
              placeholder="Address (e.g. In Zip 19060)"
              value={address}
              onChangeText={setAddress}
              autoCapitalize="sentences"
              autoCorrect
              placeholderTextColor="#8A8A8E"
              returnKeyType="next"
            />

            <TextInput
              style={mystyles.input}
              placeholder='Status ("Open" or "Closed")'
              value={status}
              onChangeText={(text) => setStatus(text)}
              autoCapitalize="words"
              placeholderTextColor="#8A8A8E"
              returnKeyType="done"
            />

            <View style={mystyles.buttonWrapper}>
              <Button
                title={submitting ? 'Submitting...' : 'Save Dish'}
                onPress={handleSubmit}
                disabled={!canSubmit}
              />
            </View>

            {!!message && (
              <Text style={mystyles.message} accessibilityLiveRegion="polite">
                {message}
              </Text>
            )}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#fff' },
  flex: { flex: 1 },
  scroll: { padding: 16 },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  title: { fontSize: 22, fontWeight: '600', marginBottom: 12 },
  input: {
    borderWidth: 1,
    borderColor: '#D1D1D6',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 12,
    fontSize: 16,
    color: '#111',
  },
  buttonWrapper: { marginTop: 8, marginBottom: 8 },
  message: { marginTop: 8, color: '#cc0000' },
});

export default CreateDishScreen;
